import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, CreditCard, Clock, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  awaiting_authorization: { label: "Aguardando confirmação do MP", color: "blue",    icon: Clock },
  trialing:  { label: "Trial ativo",      color: "amber",   icon: Clock },
  active:    { label: "Assinatura ativa", color: "emerald", icon: CheckCircle2 },
  past_due:  { label: "Pagamento pendente", color: "red",   icon: AlertCircle },
  cancelled: { label: "Cancelada",        color: "red",     icon: XCircle },
  none:      { label: "Sem assinatura",   color: "zinc",    icon: CreditCard },
};

const COLOR_CLASSES = {
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", chip: "bg-emerald-600 text-white", text: "text-emerald-800" },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   chip: "bg-amber-500 text-white",   text: "text-amber-800" },
  red:     { border: "border-red-200",     bg: "bg-red-50",     chip: "bg-red-600 text-white",     text: "text-red-800" },
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    chip: "bg-blue-600 text-white",    text: "text-blue-800" },
  zinc:    { border: "border-zinc-200",    bg: "bg-white",      chip: "bg-zinc-800 text-white",    text: "text-zinc-700" },
};

const TIER_LABEL = { micro: "Micro", start: "Start", pro: "Pro", business: "Business", enterprise: "Enterprise", agencias: "Agências" };

export default function SubscriptionWidget({ onRefresh }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Navigate to pricing page tiers; if already on /pricing, smooth-scroll to them.
  const goToPricing = () => {
    if (location.pathname === "/pricing") {
      const el = document.getElementById("pricing-tiers");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/pricing#pricing-tiers");
      setTimeout(() => {
        const el = document.getElementById("pricing-tiers");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/billing/subscription");
      setSub(data);
    } catch {
      setSub({ status: "none" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const daysLeft = () => {
    if (!sub?.trial_ends_at) return null;
    const ms = new Date(sub.trial_ends_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  };

  const handleCancel = async () => {
    const ok = window.confirm(
      "Cancelar sua assinatura Magic SIP?\n\n" +
      "• Publicações novas ficarão bloqueadas\n" +
      "• Produtos já publicados na Nuvemshop terão a IMAGEM substituída por um banner de 'Plano suspenso' e a descrição substituída por texto informativo\n" +
      "• Preço, SKU e nome permanecem intactos\n" +
      "• Ao renovar, tudo é restaurado automaticamente sem gastar créditos de IA\n\n" +
      "Confirmar cancelamento?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post("/billing/cancel");
      const swapped = data?.swap?.swapped ?? 0;
      toast.info(
        `Assinatura cancelada. ${swapped > 0 ? `${swapped} produtos tiveram imagem/descrição substituídas.` : "Nenhum produto ativo na Nuvemshop foi afetado."}`,
        { duration: 8000 }
      );
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error("Erro ao cancelar: " + formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/restore");
      toast.success(`${data.restored || 0} produtos restaurados.`);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error("Erro ao restaurar: " + formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-zinc-200 bg-white rounded-md p-4 mb-6 animate-pulse" data-testid="subscription-widget-loading">
        <div className="h-4 w-32 bg-zinc-200 rounded mb-2" />
        <div className="h-6 w-48 bg-zinc-200 rounded" />
      </div>
    );
  }

  const status = sub?.status || "none";
  const meta = STATUS_META[status] || STATUS_META.none;
  // Defensive fallback — if a future status is added with an unmapped color
  // (e.g. "blue" before this map was updated), gracefully fall back to zinc
  // so the whole page never crashes with "Cannot read properties of undefined".
  const colors = COLOR_CLASSES[meta.color] || COLOR_CLASSES.zinc;
  const Icon = meta.icon;
  const tier = TIER_LABEL[sub?.tier] || "";
  const trialProgress = sub?.trial_products_count || 0;
  const trialLimit = sub?.trial_product_limit || 10;
  const trialProgressPct = Math.min(100, (trialProgress / trialLimit) * 100);

  return (
    <div
      className={`border rounded-md p-5 mb-6 ${colors.border} ${colors.bg}`}
      data-testid="subscription-widget"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${colors.chip}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="eyebrow mb-0.5">Assinatura Magic SIP</div>
            <div className={`text-sm font-semibold ${colors.text}`} data-testid="subscription-status-label">
              {meta.label} {tier && <span className="font-mono text-xs ml-1">· {tier}</span>}
            </div>
            {status === "trialing" && sub?.trial_ends_at && (
              <div className="text-xs text-amber-900/70 mt-0.5" data-testid="trial-countdown">
                <strong>{daysLeft()}</strong> dias restantes de trial · encerra em {new Date(sub.trial_ends_at).toLocaleDateString("pt-BR")}
              </div>
            )}
            {status === "active" && sub?.current_period_end && (
              <div className="text-xs text-emerald-900/70 mt-0.5">
                Próxima cobrança em {new Date(sub.current_period_end).toLocaleDateString("pt-BR")}
              </div>
            )}
            {(status === "cancelled" || status === "past_due") && sub?.cancelled_at && (
              <div className="text-xs text-red-900/70 mt-0.5">
                Suspensa em {new Date(sub.cancelled_at).toLocaleString("pt-BR")}. Produtos ocultados por placeholder.
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {status === "none" && (
            <Button
              onClick={goToPricing}
              className="rounded-md brand-primary text-white h-9"
              data-testid="subscription-subscribe-btn"
            >
              <Zap className="w-4 h-4 mr-2" /> Assinar agora
            </Button>
          )}
          {(status === "trialing" || status === "active") && (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={busy}
              className="rounded-md border-red-300 text-red-700 hover:bg-red-100 h-9"
              data-testid="subscription-cancel-btn"
            >
              Cancelar assinatura
            </Button>
          )}
          {(status === "cancelled" || status === "past_due") && (
            <>
              <Button
                onClick={goToPricing}
                className="rounded-md brand-primary text-white h-9"
                data-testid="subscription-renew-btn"
              >
                <Zap className="w-4 h-4 mr-2" /> Renovar assinatura
              </Button>
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={busy}
                className="rounded-md h-9"
                data-testid="subscription-restore-btn"
                title="Se você já renovou fora do app, clique para restaurar imagens agora"
              >
                Restaurar imagens
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Trial progress bar */}
      {status === "trialing" && (
        <div className="mt-4" data-testid="trial-progress">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-amber-900 font-mono">
              Produtos publicados no trial: <strong>{trialProgress}</strong> / {trialLimit}
            </span>
            {trialProgress >= trialLimit && (
              <span className="text-red-700 font-semibold">Limite atingido</span>
            )}
          </div>
          <div className="h-2 bg-amber-100 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${trialProgress >= trialLimit ? "bg-red-500" : "bg-amber-500"}`}
              style={{ width: `${trialProgressPct}%` }}
            />
          </div>
          {trialProgress >= trialLimit && (
            <div className="text-xs text-red-800 mt-2">
              Confirme sua assinatura para desbloquear publicações ilimitadas (dentro da faixa do plano).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
